import React, { createContext, useState, useContext } from 'react'
import { gql, useQuery } from '@apollo/client'
import { set } from 'zod'
export const GET_SITE_ENABLED_LANGUAGES = gql`
  query GET_SITE_ENABLED_LANGUAGES {
    findFirstShopSetting {
      id
      enabled_languages {
        id
        language
        web_address_enabled
        web_address
        default_language
      }
    }
  }
`

interface LanguageContextValue {
  language: string
  setLanguage: (lang: string) => void
  allLanguages: string[]
  languageId: number
  webAddress: string
  /**
   * True while GET_SITE_ENABLED_LANGUAGES's initial fetch is in flight (i.e.
   * `allLanguages` may still be `[]` even though a real answer is coming).
   * The Router remounts `LanguageProvider` on every route change (see
   * `Router.tsx` / `config/dashboard.ts`), so this goes true->false again on
   * every navigation into a fresh route, not just on first app load.
   * Consumers that must not act on an empty `allLanguages` before it's had a
   * chance to load (e.g. `QuickCreatePageDialog`) should gate on this flag
   * rather than on `allLanguages.length === 0` alone, which can't distinguish
   * "still loading" from "loaded and genuinely empty".
   */
  languagesLoading: boolean
}

const allLanguages = ['en', 'lt']

const LanguageContext = createContext<LanguageContextValue>({
  language: 'en',
  setLanguage: () => {},
  allLanguages: [],
  languageId: 0,
  webAddress: '',
  languagesLoading: true,
})

export function LanguageProvider({ children }: { children: React.ReactNode }) {
  const [language, setLanguage] = useState('en')
  const { data, loading: languagesLoading } = useQuery(
    GET_SITE_ENABLED_LANGUAGES
  )
  const [allLanguages, setLanguages] = useState<string[]>([])
  const [languageId, setLanguageId] = useState(0)
  const [webAddress, setWebAddress] = useState('')

  React.useEffect(() => {
    if (data?.findFirstShopSetting?.enabled_languages) {
      const defaultLang = data.findFirstShopSetting.enabled_languages.find(
        (l: any) => l.default_language === true
      )
      if (defaultLang) {
        setLanguage(defaultLang.language)
        setWebAddress(defaultLang.web_address)
      }
      setLanguages(
        data.findFirstShopSetting.enabled_languages.map((l: any) => l.language)
      )
    }
  }, [data])

  React.useEffect(() => {
    setLanguageId(allLanguages.indexOf(language))

    const webAddress = data?.findFirstShopSetting?.enabled_languages.find(
      (l: any) => l.language === language
    )?.web_address
    if (!webAddress) {
      // find default language
      const defaultLang = data?.findFirstShopSetting?.enabled_languages.find(
        (l: any) => l.default_language === true
      )
      console.log(
        'Default language:',
        defaultLang,
        data?.findFirstShopSetting?.enabled_languages
      )

      setWebAddress(defaultLang?.web_address)
    }
  }, [language, allLanguages])

  return (
    <LanguageContext.Provider
      value={{
        language,
        setLanguage,
        allLanguages,
        languageId,
        webAddress,
        languagesLoading,
      }}
    >
      {children}
    </LanguageContext.Provider>
  )
}

export function useLanguage() {
  return useContext(LanguageContext)
}
